import clsx from 'clsx';
import React from 'react';
import { BiSolidLock } from 'react-icons/bi';

import { getServiceId } from '@/api/tokens.helper';
import { type ActionMenuItem, ActionsButton } from '@/components/ActionsButton/ActionsButton';
import { isTaskDueSoon, isTaskExpired } from '@/components/TaskExpiringLabel/taskExpiration';
import { TaskExpiredLabel } from '@/components/TaskExpiringLabel/TaskExpiredLabel';
import { TaskExpiringLabel } from '@/components/TaskExpiringLabel/TaskExpiringLabel';
import TaskStatusesBadge from '@/components/TaskStatusesBadge/TaskStatusesBadge';
import { type TaskResponse, TaskStatus } from '@/store/tasks/tasks.dto';
import { formatIsoToTime12h } from '@/utils/timeFormatter';
import StartIcon from '@assets/stars-ai.svg?react';

import styles from './MaintenanceTaskCard.module.scss';

interface MaintenanceTaskCardProps {
  task: TaskResponse;
  isServiceProvider?: boolean;
  isDark?: boolean;
  hideStatusBadge?: boolean;
  /** Which statuses the chip offers; the maintenance widget cuts it to To do / Done. */
  statusOptions?: readonly TaskStatus[];
  onTaskStatusChange?: (taskId: string, serviceId: string, status: TaskStatus) => void;
  onEdit?: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
  onClick?: () => void;
}

export const MaintenanceTaskCard: React.FC<MaintenanceTaskCardProps> = ({
  task,
  isServiceProvider,
  isDark,
  hideStatusBadge,
  statusOptions,
  onTaskStatusChange,
  onEdit,
  onDelete,
  onClick,
}) => {
  const taskMenuItems: ActionMenuItem[] = [
    { label: 'Edit', onClick: () => onEdit?.(task.id) },
    { label: 'Delete', onClick: () => onDelete?.(task.id), variant: 'danger' },
  ];

  return (
    <div
      className={`${styles.taskItem} ${isDark ? styles.dark : ''}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <div className={styles.lockCol}>{task.visibility === 'private' && <BiSolidLock />}</div>
      <div className={styles.taskContent}>
        <div className={styles.taskTitle}>{task.title}</div>
        <div className={styles.taskTime}>
          {formatIsoToTime12h(task.time_slot.start)} - {formatIsoToTime12h(task.time_slot.end)}
        </div>
        {/* <div className={styles.taskSubtitle}>{task.description}</div> */}
      </div>
      <div className={styles.taskActions} onClick={(e) => e.stopPropagation()}>
        {/* The chips wrap as a group; the menu beside them does not, so it keeps the
            row's first line however many lines the chips take. */}
        <div className={styles.taskChips}>
          {isTaskExpired(task) && <TaskExpiredLabel task={task} />}
          {isTaskDueSoon(task) && <TaskExpiringLabel task={task} />}
          {!hideStatusBadge && (
            <TaskStatusesBadge
              status={task.status as TaskStatus}
              isServiceProvider={isServiceProvider}
              options={statusOptions}
              onChange={(status) => onTaskStatusChange?.(task.id, getServiceId(), status)}
            />
          )}
          {task.is_ai_generated && (
            <div className={clsx(styles.ai, 'body-small')}>
              <StartIcon />
            </div>
          )}
        </div>
        {onEdit && onDelete && !isServiceProvider && (
          <div className={styles.taskMenu}>
            <ActionsButton items={taskMenuItems} variant={isDark ? 'lite' : 'dark'} />
          </div>
        )}
      </div>
    </div>
  );
};
